/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { SLRSpinner } from '@slr/shared-library';
import { PROFILE_ORGANIZATIONS_PATH } from 'configs';
import { useFindMembershipsOfUser } from 'hooks';
import { FC } from 'react';
import { Navigate } from 'react-router';
import { OrganizationsPage } from './OrganizationsPage';

/**
 * Simple redirect component that displays the organizations list.
 * If user has multiple organizations, shows the list instead of redirecting to the first one.
 */
export const OrganizationRedirect: FC = () => {
  const { data: memberships, isPending, isError } = useFindMembershipsOfUser();

  if (isPending) {
    return <SLRSpinner />;
  }

  if (isError || !memberships || memberships.length === 0) {
    return <OrganizationsPage />;
  }

  if (memberships.length > 1) {
    return <OrganizationsPage />;
  }

  const firstOrganization = memberships[0];
  return <Navigate to={`${PROFILE_ORGANIZATIONS_PATH}/${firstOrganization.organizationId}`} replace />;
};
